import React, { useEffect, useMemo, useState } from "react";
import { resolveAvatarUrl, handleAvatarError } from "../../utils/profilePicUrl";
import AuthenticatedNavbar from "../headers/AuthenticatedNavbar";
import { getApiBaseUrl } from "../../config/api";
import { isAdmin } from "../../utils/userRole";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./UserManager.css";

const API_URL = getApiBaseUrl();

function SearchIcon() {
  return (
    <svg className="um-search__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 4v10m0 0l-4-4m4 4l4-4M5 18h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a2 2 0 01-2 2H9a2 2 0 01-2-2V7h10z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function UserManager({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(
          `${API_URL}/users/get_users.php?admin_id=${encodeURIComponent(user?.id || '')}`
        );
        const data = await res.json();
        if (data.success) {
          setUsers(data.users);
          setFiltered(data.users);
        } else throw new Error("Failed to fetch users");
      } catch (err) {
        console.error(err);
        setError("Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user?.id]);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    const filteredData = users.filter((u) => {
      if (!q) return true;
      const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const country = String(u.country || "").toLowerCase();
      return name.includes(q) || email.includes(q) || country.includes(q);
    });
    setFiltered(filteredData);
    setCurrentPage(1);
  }, [search, users]);

  const roleCounts = useMemo(() => {
    const counts = { admin: 0, employer: 0, intern: 0, user: 0 };
    users.forEach((u) => {
      const role = String(u.typeofuser || "user").toLowerCase();
      if (counts[role] !== undefined) counts[role] += 1;
      else counts.user += 1;
    });
    return counts;
  }, [users]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const res = await fetch(`${API_URL}/users/delete_user.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ admin_id: user?.id, id }),
      });
      const data = await res.json();
      if (data.success) {
        setUsers((prev) => prev.filter((u) => u.id !== id));
        alert("User deleted successfully!");
      } else {
        alert(data.message || "Failed to delete user");
      }
    } catch {
      alert("Error deleting user");
    }
  };

  const handleChangeType = async (id, newType) => {
    try {
      const res = await fetch(`${API_URL}/users/update_user_type.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ admin_id: user?.id, id, typeofuser: newType }),
      });
      const data = await res.json();
      if (data.success) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, typeofuser: newType } : u))
        );
        alert("User type updated!");
      } else alert(data.message || "Failed to update user type");
    } catch {
      alert("Error updating user type");
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setEditForm({
      first_name: u.first_name,
      last_name: u.last_name,
      password: "",
    });
  };

  const handleSaveEdit = async () => {
    const { first_name, last_name, password } = editForm;
    if (!first_name || !last_name)
      return alert("First and last name cannot be empty");

    try {
      const res = await fetch(`${API_URL}/users/update_user_info.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          admin_id: user?.id,
          id: editingUser.id,
          first_name,
          last_name,
          password,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === editingUser.id ? { ...u, first_name, last_name } : u
          )
        );
        alert("User info updated successfully!");
        setEditingUser(null);
      } else alert("Failed to update user info");
    } catch {
      alert("Error updating user info");
    }
  };

  const downloadExcel = () => {
    if (users.length === 0) {
      alert("No users to export.");
      return;
    }

    const excelData = users.map((u) => ({
      ID: u.id,
      Name: `${u.first_name} ${u.last_name}`,
      Email: u.email,
      Country: u.country,
      Type: u.typeofuser,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "user_list.xlsx");
  };

  const start = (currentPage - 1) * usersPerPage;
  const currentUsers = filtered.slice(start, start + usersPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / usersPerPage));
  const canManage = isAdmin(user);

  const renderPageButtons = () => {
    const buttons = [];
    for (let i = 0; i < totalPages; i++) {
      if (totalPages > 10) {
        if (
          i < 3 ||
          i >= totalPages - 3 ||
          (i >= currentPage - 2 && i <= currentPage)
        ) {
          buttons.push(
            <button
              key={i}
              type="button"
              className={`um-page-btn${currentPage === i + 1 ? " is-active" : ""}`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          );
        } else if (
          (i === 3 && currentPage > 5) ||
          (i === totalPages - 4 && currentPage < totalPages - 4)
        ) {
          buttons.push(
            <span key={`e-${i}`} className="um-page-ellipsis">
              …
            </span>
          );
        }
      } else {
        buttons.push(
          <button
            key={i}
            type="button"
            className={`um-page-btn${currentPage === i + 1 ? " is-active" : ""}`}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        );
      }
    }
    return buttons;
  };

  if (loading) {
    return (
      <>
        <AuthenticatedNavbar user={user} onLogout={onLogout} />
        <div className="um-page">
          <p className="um-status">Loading users…</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AuthenticatedNavbar user={user} onLogout={onLogout} />
        <div className="um-page">
          <p className="um-status um-status--error">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthenticatedNavbar user={user} onLogout={onLogout} />
      <div className="um-page">
        <div className="um-shell">
          <header className="um-header">
            <div>
              <h1>User Management</h1>
              <p>Search members, update roles, and keep the community directory current.</p>
            </div>
            <div className="um-stats" aria-label="User statistics">
              <div className="um-stat">
                <div className="um-stat__value">{filtered.length}</div>
                <div className="um-stat__label">
                  {search.trim() ? "Matches" : "Total"}
                </div>
              </div>
              <div className="um-stat">
                <div className="um-stat__value">{roleCounts.admin}</div>
                <div className="um-stat__label">Admins</div>
              </div>
              <div className="um-stat">
                <div className="um-stat__value">{roleCounts.employer}</div>
                <div className="um-stat__label">Employers</div>
              </div>
              <div className="um-stat">
                <div className="um-stat__value">{roleCounts.intern + roleCounts.user}</div>
                <div className="um-stat__label">Members</div>
              </div>
            </div>
          </header>

          <section className="um-card">
            <div className="um-toolbar">
              <div className="um-search">
                <SearchIcon />
                <input
                  type="search"
                  placeholder="Search by name, email, or country…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search users"
                />
              </div>
              <button type="button" className="um-export" onClick={downloadExcel}>
                <DownloadIcon />
                Export Excel
              </button>
            </div>

            <div className="um-table-wrap">
              {currentUsers.length === 0 ? (
                <div className="um-empty">
                  <strong>No users found</strong>
                  Try a different search term.
                </div>
              ) : (
                <table className="um-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Email</th>
                      <th>Country</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map((u) => {
                      const role = String(u.typeofuser || "user").toLowerCase();
                      return (
                        <tr key={u.id}>
                          <td>
                            <div className="um-user">
                              <img
                                className="um-avatar"
                                src={resolveAvatarUrl(u, API_URL)}
                                alt=""
                                onError={handleAvatarError}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="um-user__name">
                                  {u.first_name} {u.last_name}
                                </div>
                                {u.username ? (
                                  <div className="um-user__meta">@{u.username}</div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="um-email">{u.email || "—"}</td>
                          <td
                            className={
                              u.country ? "um-country" : "um-country um-country--empty"
                            }
                          >
                            {u.country || "Not set"}
                          </td>
                          <td>
                            <select
                              className="um-role"
                              data-role={role}
                              value={role}
                              onChange={(e) => handleChangeType(u.id, e.target.value)}
                              aria-label={`Role for ${u.first_name || "user"}`}
                            >
                              <option value="user">User</option>
                              <option value="intern">Intern</option>
                              <option value="employer">Employer</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td>
                            {canManage && (
                              <div className="um-actions">
                                <button
                                  type="button"
                                  className="um-btn um-btn--edit"
                                  onClick={() => handleEdit(u)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="um-btn um-btn--delete"
                                  onClick={() => handleDelete(u.id)}
                                  title="Delete user"
                                  aria-label={`Delete ${u.first_name || "user"}`}
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {filtered.length > 0 && (
              <div className="um-pagination">
                <button
                  type="button"
                  className="um-page-btn"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {renderPageButtons()}
                <button
                  type="button"
                  className="um-page-btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>

        {editingUser && (
          <div
            className="um-modal-overlay"
            onClick={() => setEditingUser(null)}
            role="presentation"
          >
            <div
              className="um-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="um-edit-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="um-edit-title">Edit user</h3>
              <p className="um-modal__sub">
                Update name or set a new password for {editingUser.email}.
              </p>
              <div className="um-field">
                <label htmlFor="um-first-name">First name</label>
                <input
                  id="um-first-name"
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, first_name: e.target.value })
                  }
                />
              </div>
              <div className="um-field">
                <label htmlFor="um-last-name">Last name</label>
                <input
                  id="um-last-name"
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, last_name: e.target.value })
                  }
                />
              </div>
              <div className="um-field">
                <label htmlFor="um-password">New password</label>
                <input
                  id="um-password"
                  type="password"
                  value={editForm.password}
                  onChange={(e) =>
                    setEditForm({ ...editForm, password: e.target.value })
                  }
                  placeholder="Leave blank to keep current password"
                />
              </div>
              <div className="um-modal__actions">
                <button
                  type="button"
                  className="um-btn um-btn--ghost"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="um-btn um-btn--primary"
                  onClick={handleSaveEdit}
                >
                  Save changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
